import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, getRememberedUsername } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Lock, User as UserIcon } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const remembered = getRememberedUsername();
  const [username, setUsername] = useState(remembered || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(Boolean(remembered));
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username, password, remember);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 vsm-shell">
      {/* Left panel: brand (hidden on mobile) */}
      <div className="hidden lg:flex lg:col-span-3 vsm-grid-pattern relative p-12 flex-col justify-between border-r vsm-border">
        <div className="flex items-center gap-4">
          <img
            src="/ramki-logo.jpg"
            alt="Ramki Technologies"
            data-testid="login-company-logo"
            className="h-16 w-auto object-contain bg-white p-1"
          />
          <div>
            <div className="vsm-overline">Fleet Operations</div>
            <div className="font-bold text-lg tracking-tight">VEHICLE SIM MAINTENANCE</div>
          </div>
        </div>

        <div className="max-w-2xl">
          <div className="vsm-overline mb-4">Ramki Technologies Pvt. Ltd. · Operations Console</div>
          <h1
            className="text-5xl lg:text-6xl font-bold tracking-tight leading-none mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Track every SIM,
            <br />
            every vehicle,
            <br />
            every renewal.
          </h1>
          <p className="vsm-muted text-base max-w-md leading-relaxed">
            A precise, no-nonsense workspace for fleet engineers managing SIM activations,
            device installations and customer invoicing across thousands of assets.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6 max-w-xl">
          <Metric label="Countries" value="249+" />
          <Metric label="Masters" value="5" />
          <Metric label="Audit fields" value="∞" />
        </div>
      </div>

      {/* Right panel: form */}
      <div className="lg:col-span-2 flex items-center justify-center p-4 sm:p-6 lg:p-10 vsm-surface min-h-screen">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm space-y-6 sm:space-y-8"
        >
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center gap-3 pb-2 border-b vsm-border">
            <img src="/ramki-logo.jpg" alt="Ramki Technologies" className="h-12 w-auto object-contain bg-white p-1" />
            <div>
              <div className="vsm-overline">Fleet Operations</div>
              <div className="font-bold text-xs tracking-tight" style={{ color: "#1d4ed8" }}>RAMKI TECHNOLOGIES</div>
            </div>
          </div>

          <div>
            <div className="vsm-overline">Sign in</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">Operations Login</h2>
            <p className="text-sm vsm-muted mt-2">
              Username is not case-sensitive. Passwords are.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="vsm-overline">Username</Label>
              <div className="relative mt-2">
                <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 vsm-muted" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="rounded-none pl-9 h-11"
                  required
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="vsm-overline">Password</Label>
              <div className="relative mt-2">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 vsm-muted" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none pl-9 h-11"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="login-remember-label">
              <Checkbox
                checked={remember}
                onCheckedChange={(v) => setRemember(Boolean(v))}
                data-testid="login-remember-checkbox"
                className="rounded-none"
                id="remember-me"
              />
              <span className="text-sm">Remember me on this device</span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="w-full h-11 rounded-none vsm-primary-bg hover:opacity-90 font-semibold tracking-wide"
          >
            {busy ? "SIGNING IN…" : "SIGN IN →"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border-l-2 vsm-border pl-4">
      <div
        className="text-3xl font-bold tracking-tight"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {value}
      </div>
      <div className="vsm-overline mt-1">{label}</div>
    </div>
  );
}
